import { findAttitude, findExpression, LENGTH_PHRASES, SLEEVE_PHRASES, findGrade, findLighting, findPose, findPov, resolveScene, resolveStock, } from './presets.js';
import { extraGarmentReferences, pickView, garmentViews } from './garments.js';
/**
 * Deterministic prompt composition.
 *
 * The Prompt Architect agent normally writes the outfit paragraph, but every
 * prompt is *assembled* here so the structure is identical across a shoot and
 * across providers. Structure is copied from the reference prompt this app was
 * built around: subject → outfit → backdrop, then labelled Pose / Lighting /
 * Camera / Details lines, then the quality tail, then reference bindings.
 */
const QUALITY_TAIL = 'Photorealistic, hyperdetailed, 8K, magazine quality, clean edges, clothing swap ready';
const DEFAULT_DETAILS = 'natural skin texture, realistic fabric folds, subtle creasing, authentic proportions, no stylization';
export function ratioPhrase(ratio) {
    const orientation = ratio === '2:3' || ratio === '3:4' || ratio === '4:5' || ratio === '9:16'
        ? 'portrait'
        : ratio === '1:1'
            ? 'square'
            : 'landscape';
    return `${ratio} ${orientation} ratio`;
}
/** The studio ratio closest to a photograph's own shape; 2:3 when the shape is unknown. */
export function nearestAspectRatio(width, height) {
    if (!width || !height)
        return '2:3';
    const shape = width / height;
    const ratios = [
        ['2:3', 2 / 3],
        ['3:4', 3 / 4],
        ['4:5', 4 / 5],
        ['1:1', 1],
        ['4:3', 4 / 3],
        ['16:9', 16 / 9],
        ['9:16', 9 / 16],
    ];
    return ratios.reduce((best, next) => Math.abs(Math.log(next[1] / shape)) < Math.abs(Math.log(best[1] / shape)) ? next : best)[0];
}
/**
 * How she is placed in a photograph, from the words the reader wrote about
 * it. A pose photograph has no preset to say "seated", and a scene told she
 * stands puts a seated pose on its feet.
 */
export function postureOf(pose) {
    const text = pose.toLowerCase();
    if (/\b(lying|lies|reclin|lounging)/.test(text))
        return 'reclining';
    if (/\b(sit|sits|sitting|seated|perched)/.test(text))
        return 'seated';
    if (/\b(crouch|squat|kneel)/.test(text))
        return 'crouching';
    if (/\b(lean|leans|leaning)/.test(text))
        return 'leaning';
    if (/\b(walk|walks|walking|strid|mid-step)/.test(text))
        return 'walking';
    return 'standing';
}
/**
 * Builds the canonical identity fragment for a model, e.g.
 * "a white woman with dark brown, loosely waved hair and natural-looking makeup".
 * Used when the user has not written one by hand.
 */
export function buildIdentityPrompt(a) {
    const noun = a.gender === 'female' ? 'woman' : a.gender === 'male' ? 'man' : 'person';
    const lead = [a.ethnicity, noun].filter(Boolean).join(' ').trim();
    const parts = [];
    const hair = [a.hairColor, a.hairLength, a.hairStyle]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join(', ');
    if (hair)
        parts.push(`${hair} hair`);
    if (a.eyeColor?.trim())
        parts.push(`${a.eyeColor.trim()} eyes`);
    if (a.makeup?.trim())
        parts.push(a.makeup.trim());
    if (a.distinguishingFeatures?.trim())
        parts.push(a.distinguishingFeatures.trim());
    const descriptors = parts.length ? ` with ${joinList(parts)}` : '';
    const frame = [a.ageRange?.trim(), a.build?.trim(), a.height?.trim()].filter(Boolean).join(', ');
    const framePhrase = frame ? `, ${frame} build` : '';
    return `a ${lead}${descriptors}${framePhrase}`.replace(/\s+/g, ' ').trim();
}
export function joinList(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    if (items.length === 2)
        return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
/**
 * The lines that tie prompt language to the uploaded reference photos, e.g.
 * "The woman is wearing the vest in image 1 and the dark wash denim trousers
 * in image 2."
 *
 * `referenceOrder` is the order the images are attached to the render request,
 * which is what the numbering has to match.
 */
export function buildReferenceBindings(subjectNoun, referenceOrder, textOnlyItems, 
/** Identity photos, attached immediately after the garments. */
faceReferenceCount = 0, 
/** A background photograph, attached last. */
hasBackgroundReference = false, 
/**
 * What each garment image actually shows, in the same order. A back
 * photograph attached without saying so is read as a front one, and the
 * renderer turns the garment around to match it.
 */
viewKinds = [], 
/** The rejected frame, attached last, for a retry that corrects rather than re-rolls. */
hasPreviousFrame = false, 
/** Ask for the face plainly, for the attempt after the renderer declined. */
plainIdentity = false, 
/** Sunglasses are part of the look, so the eyes in the reference will be covered. */
wearsSunglasses = false, 
/** An approved frame from this roll, attached right after the identity photos. */
hasAnchor = false, 
/** Full-body photographs of her, the side this frame sees first, attached right after the identity photos. */
bodyViews = [], 
/** The photograph being recreated, attached after the approved frame. */
referencePost, 
/**
 * The other photographs of the pieces, attached after the background: whose
 * piece each is, which side or a close-up, and where a close-up is.
 */
extras = []) {
    /** Images after the faces shift by one for each full-body view attached. */
    const body = bodyViews.length;
    const post = referencePost ? 1 : 0;
    const lines = [];
    const names = disambiguate(referenceOrder);
    const clauses = names.map((name, i) => `the ${name} in image ${i + 1}`);
    if (clauses.length) {
        lines.push(`The ${subjectNoun} is wearing ${joinList(clauses)}.`);
    }
    if (textOnlyItems.length) {
        lines.push(`Also wearing ${joinList(textOnlyItems)}.`);
    }
    if (clauses.length) {
        lines.push('Reproduce the garments from the reference images exactly: same colour, same fabric, same pattern placement, same cut and same trims. Do not restyle, recolour or redesign them.');
        /*
         * The photographs are the description. The analyst's checklist used to be
         * written out here as well — colours, print scale, button counts — and
         * where the words and the photograph disagreed the render split the
         * difference. The critic still marks the frame against that checklist.
         */
        lines.push('Every feature in the photographs must be in the render, at the same size, density and placement. Add nothing that is not in them: no extra buttons, pockets, seams, logos or trims.');
        names.forEach((name, i) => {
            const kind = viewKinds[i];
            if (!kind || kind === 'front')
                return;
            lines.push(kind === 'detail'
                ? `Image ${i + 1} is a close photograph of the ${name}'s surface, not the whole piece — take the weave, pattern and finish from it.`
                : `Image ${i + 1} shows the ${kind} of the ${name}. This frame looks at that ${kind}, so reproduce what that photograph shows.`);
        });
    }
    if (faceReferenceCount > 0) {
        const first = referenceOrder.length + 1;
        const last = referenceOrder.length + faceReferenceCount;
        const which = faceReferenceCount === 1 ? `Image ${first}` : `Images ${first}-${last}`;
        if (plainIdentity) {
            /*
             * The attempt after a refusal. "Reproduce that exact face … it must be
             * recognisably the same person" is the line most likely to make a
             * renderer decline a photograph of a real person; asked plainly, as a
             * reference for the model's look, it usually draws.
             */
            lines.push(`${which} ${faceReferenceCount === 1 ? 'shows' : 'show'} the model for this shoot. She is the same woman in this photograph: use ${faceReferenceCount === 1 ? 'it' : 'them'} for her face and hair, and ignore the clothes and the background in ${faceReferenceCount === 1 ? 'it' : 'them'}.`);
        }
        else {
            // Without this the renderer has no idea what the portrait is, and will
            // happily copy the plain t-shirt it was shot in into the final photograph.
            lines.push(`${which} ${faceReferenceCount === 1 ? 'is' : 'are'} an identity reference of the ${subjectNoun}, not clothing. Reproduce that exact face: same facial structure, same eyes, nose and mouth, same skin tone, same hair colour and texture. It must be recognisably the same person.`, `Ignore whatever ${faceReferenceCount === 1 ? 'this identity image shows' : 'these identity images show'} her wearing, and ignore its background and framing. Take only the person from ${faceReferenceCount === 1 ? 'it' : 'them'}.`);
        }
        /*
         * "Same eyes" and a pair of sunglasses ask for two things that cannot both
         * be in the picture. Said outright, the glasses win and the rest of the
         * face is still held to the photograph.
         */
        if (wearsSunglasses) {
            lines.push('She wears sunglasses in this frame, so her eyes are hidden behind the lenses; match the rest of her face — brows, nose, mouth, jaw, skin and hair — to the reference.');
        }
    }
    bodyViews.forEach((bodyView, i) => {
        const index = referenceOrder.length + faceReferenceCount + i + 1;
        /*
         * The portraits hold the face; this holds the rest of her. Without it the
         * renderer invents a body to go under the face — taller, slimmer, the
         * hair a different length from behind — and a back framing, which shows
         * no face at all, had nothing of her to go on but the words.
         */
        lines.push(`Image ${index} is a full-body photograph of the same ${subjectNoun}, seen ${BODY_SIDE[bodyView]}. Match her from it: her height, build and proportions — shoulders, waist, hips and legs — and her hair as it looks from that side, its length, volume and how it falls. Ignore what she wears in it, her pose, the background and the light.`);
    });
    if (hasAnchor) {
        const index = referenceOrder.length + faceReferenceCount + body + 1;
        /*
         * An approved frame from this same roll. Every frame used to be shot from
         * the references alone, so each one re-invented the parts the references
         * leave open — the handbag, the jewellery, how the cardigan sits, and
         * sometimes the face — and a series of eight read as several shoots.
         * One frame that came out right is the most exact description of the
         * rest there can be.
         */
        lines.push(`Image ${index} is an approved earlier frame from this same shoot. The person, the outfit and the accessories in it are right: the same woman with the same face and hair, the same garments worn the same way, and the same bag, jewellery and glasses — the identical objects, not similar ones. Take nothing else from it: not its pose, framing, crop, background or light.`);
    }
    if (referencePost) {
        const index = referenceOrder.length + faceReferenceCount + body + (hasAnchor ? 1 : 0) + 1;
        const people = faceReferenceCount > 0
            ? `the model is the ${subjectNoun} in ${faceReferenceCount === 1 ? `image ${referenceOrder.length + 1}` : `images ${referenceOrder.length + 1}-${referenceOrder.length + faceReferenceCount}`}`
            : `the model is the ${subjectNoun} described above`;
        /*
         * The post is the photograph to make again — everything about it except
         * the person and the clothes. Both exclusions are said outright: a
         * renderer shown a photograph of a woman in a coat draws that woman in
         * that coat unless it is told which of the two to take from elsewhere.
         */
        const keepLight = referencePost.keepLight !== false;
        lines.push(`Image ${index} is the photograph to recreate. Copy from it the pose and body position — the hands, arms, legs, head angle and gaze — the camera angle, height, distance and crop, ${keepLight ? 'the direction and quality of the light, ' : ''}and the mood${referencePost.keepScene ? ', and the location: the same place, seen from the same spot' : ''}. Do not copy the person in it: not her face, hair, skin or body — ${people}. Take none of its clothes, bag or jewellery either: she wears only the outfit and accessories described above.${keepLight ? '' : ' Do not copy its light either.'}`);
    }
    if (extras.length) {
        const first = referenceOrder.length +
            faceReferenceCount +
            body +
            (hasAnchor ? 1 : 0) +
            post +
            (hasBackgroundReference ? 1 : 0) +
            1;
        extras.forEach((extra, i) => {
            const name = names[extra.index] ?? 'garment';
            if (extra.kind === 'label')
                return;
            if (extra.kind === 'detail') {
                const where = extra.note?.trim();
                lines.push(`Image ${first + i} is a close-up of a detail of the ${name}${where ? ` — ${where}` : ''}, not a separate item. Reproduce that detail exactly — its shape, colour, material, size and placement on the piece — wherever that part shows in this frame.`);
                return;
            }
            /*
             * The sides of a piece this frame does not face still say how it is
             * made: the yoke and the print across the back, the seam down the side.
             * Named as the same piece, or the renderer dresses her in two.
             */
            lines.push(`Image ${first + i} shows the same ${name} ${GARMENT_SIDE[extra.kind]}, not a separate item. Take from it how that side is made — its cut, seams, print and trims — and keep the piece consistent with it wherever that side shows.`);
        });
    }
    if (hasPreviousFrame) {
        const index = referenceOrder.length +
            faceReferenceCount +
            body +
            (hasAnchor ? 1 : 0) +
            post +
            (hasBackgroundReference ? 1 : 0) +
            extras.length +
            1;
        /*
         * The last image, and the strongest instruction, because it has to beat
         * everything above it: the prompt still describes a photograph to take,
         * and without this the renderer takes a new one. Naming what must not
         * change is what makes this an edit instead of another roll of the dice.
         */
        lines.push(`Image ${index} is your own previous attempt at this exact frame. Start from it and edit it. Keep the same person and the same face, the same pose and body position, the same framing and crop, the same lighting, the same background, and the same colours everywhere you are not asked to change something.`, 'Change only what the correction above names. Everything else in the frame must be the same photograph — this is a retouch, not a re-shoot.');
    }
    if (hasBackgroundReference) {
        const index = referenceOrder.length + faceReferenceCount + body + (hasAnchor ? 1 : 0) + post + 1;
        // Named last so it cannot be mistaken for a garment or the model, and its
        // own occupants explicitly excluded — a location photo often has people in
        // it, and none of them belong in this frame.
        lines.push(`Image ${index} is the background: place the ${subjectNoun} in that exact setting, matching its light, depth and perspective. Take nothing else from it — no clothing, and none of the people in it.`);
    }
    return lines;
}
/** How each other side of a piece is introduced: "seen from behind". */
const GARMENT_SIDE = {
    front: 'from the front',
    back: 'from behind',
    side: 'from the side',
};
/** How each turnaround photograph is introduced: "seen from behind". */
const BODY_SIDE = {
    front: 'from the front',
    profile: 'in profile, from the side',
    back: 'from behind',
};
/**
 * Names each garment uniquely.
 *
 * Two pieces can easily analyse to the same name ("black t-shirt" twice), and
 * "the black t-shirt in image 1 and the black t-shirt in image 2" tells the
 * renderer nothing. Each fallback is tried only if it actually separates the
 * colliding items — a category qualifier is useless when both are tops.
 */
export function disambiguate(garments) {
    const names = garments.map((g) => (g.analysis?.name || g.label || g.category).trim() || 'garment');
    const groups = new Map();
    names.forEach((name, i) => {
        const key = name.toLowerCase();
        groups.set(key, [...(groups.get(key) ?? []), i]);
    });
    const out = [...names];
    for (const indices of groups.values()) {
        if (indices.length < 2)
            continue;
        // Try each qualifier in turn; keep the first that makes the group distinct.
        const candidates = [
            (i) => garments[i].label.trim() || names[i],
            (i) => `${names[i]} (${garments[i].category})`,
            (i) => `${ORDINALS[indices.indexOf(i)] ?? `${indices.indexOf(i) + 1}th`} ${names[i]}`,
        ];
        for (const build of candidates) {
            const attempt = indices.map(build);
            const distinct = new Set(attempt.map((a) => a.toLowerCase()));
            if (distinct.size === indices.length) {
                indices.forEach((globalIndex, k) => {
                    out[globalIndex] = attempt[k];
                });
                break;
            }
        }
    }
    return out;
}
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];
/**
 * Places the subject in the scene using language that matches the posture.
 *
 * "Stands casually against a summer meadow" is wrong twice over: you do not
 * stand while reclining, and you are *in* a meadow rather than against it.
 * Studio sweeps are backdrops you stand against; locations are places you are
 * inside, and a pose on the ground needs the surface named so the renderer has
 * something to put the fabric on.
 */
export function buildSceneClause(subjectNoun, scene, posture, onGroundSurface = false) {
    const surface = onGroundSurface && scene.groundSurface ? ` She is down on ${scene.groundSurface}.` : '';
    // Location descriptions are too varied for any one preposition to fit them
    // all — "in a meadow" works, "in a fence" does not — so state the setting
    // plainly and let the pose line carry the posture.
    if (scene.kind === 'location') {
        return `The setting is ${scene.prompt}.${surface}`;
    }
    const verbs = {
        standing: 'stands casually against',
        walking: 'walks across the set in front of',
        seated: 'is seated in front of',
        reclining: 'reclines on the floor in front of',
        leaning: 'leans against',
        crouching: 'crouches in front of',
    };
    return `The ${subjectNoun} ${verbs[posture]} ${scene.prompt}.${surface}`;
}
/** "image 3" or "images 3-5". */
function imagesSpan([first, last]) {
    return first === last ? `image ${first}` : `images ${first}-${last}`;
}
/**
 * A pose photograph reproduced exactly: the photograph itself, edited, with
 * three things changed — the person, the clothes, the place — rather than a
 * new photograph described to resemble it. Described in words, a pose comes
 * back approximately: the hand on the other cheek, the knee lower, the cup in
 * the wrong hand. An image model asked to change what someone wears and who
 * she is keeps the rest of the picture where it was.
 */
function poseTransferLines(o) {
    const held = o.props.length
        ? ` Keep ${joinList(o.props)} exactly where ${o.props.length === 1 ? 'it is' : 'they are'}, held the same way: part of the pose.`
        : '';
    const person = o.faces
        ? `give her the face and hair of the ${o.subjectNoun} in ${imagesSpan(o.faces)}${o.bodies ? `, and the build and proportions of the full-figure ${imagesSpan(o.bodies)}` : ''} — her head stays at the angle it has in image ${o.post}, now with her face`
        : `make her the ${o.subjectNoun} described below — her head stays at the angle it has in image ${o.post}`;
    const clothes = o.garments
        ? `remove everything she wears in image ${o.post}, bag and jewellery included, and dress her in the garments in ${imagesSpan([1, o.garments])}, exactly as photographed, fitted to this same pose — the folds and drape follow her body as it is posed`
        : `remove everything she wears in image ${o.post}, bag and jewellery included, and dress her in the outfit described below, fitted to this same pose`;
    const place = o.keepScene
        ? 'keep the place in the photograph as it is'
        : `replace everything around her with ${o.background ? `the place in image ${o.background}` : 'the setting described below'}. Whatever she sits on, leans on or stands on stays at the same height and position under her body, now part of the new place${o.keepLight ? '' : ', and the light on her is the light of that place'}`;
    return [
        `This is a pose transfer. Start from image ${o.post}, the pose photograph, and edit it: the finished photograph is that same photograph with three things changed and nothing else.`,
        `Keep exactly as in image ${o.post}: the pose, limb by limb — the tilt and turn of the head and where the eyes look, the shoulders, each arm, each hand and every finger, the torso, the hips, each leg and each foot — and the camera: its height, angle, lens, distance and crop.${held} Do not straighten, tidy or re-pose anything.`,
        `Change 1, the person: ${person}.`,
        `Change 2, the clothes: ${clothes}.`,
        `Change 3, the place: ${place}.`,
        'The finished photograph, described:',
    ];
}
/** Assembles the final prompt string from its blocks. */
export function composePrompt(blocks) {
    // Each segment is a standalone sentence, so terminate them individually
    // rather than punctuating only the end of the joined paragraph.
    const opening = [
        blocks.lead
            ? `Hyperrealistic ${blocks.lead}, worn by ${blocks.subject}`
            : `Hyperrealistic fashion editorial photograph of ${blocks.subject}`,
        blocks.outfit,
        blocks.accessories,
        blocks.backdrop,
    ]
        .map((s) => s?.trim())
        .filter(Boolean)
        .map(ensureStop)
        .join(' ');
    const lines = [
        ...(blocks.transfer?.length ? [...blocks.transfer, ''] : []),
        opening,
        `Pose: ${stripStop(blocks.pose)}.`,
        /*
         * After the pose and before anything else, because the pose line often
         * carries an expression of its own and the last word wins with a
         * renderer: this one says so outright.
         */
        ...(blocks.expression
            ? [`Expression: ${stripStop(blocks.expression)}, whatever expression the pose describes.`]
            : []),
        ...(blocks.attitude ? [`Attitude: ${stripStop(blocks.attitude)}.`] : []),
        `Lighting: ${stripStop(blocks.lighting)}.`,
        `Camera: ${stripStop(blocks.camera)}.`,
        `Details: ${stripStop(blocks.details)}.`,
        blocks.quality,
    ];
    if (blocks.referenceBindings.length) {
        lines.push('', ...blocks.referenceBindings);
    }
    return lines
        .join('\n')
        .replace(/[ \t]+\n/g, '\n')
        .trim();
}
function ensureStop(s) {
    const t = s.trim();
    if (!t)
        return t;
    return /[.!?]$/.test(t) ? t : `${t}.`;
}
function stripStop(s) {
    return s.trim().replace(/[.\s]+$/, '');
}
/** Builds the blocks for one shot, then composes them. */
export function buildShotPrompt(args) {
    const pov = findPov(args.povId);
    const pose = args.poseId ? findPose(args.poseId) : undefined;
    const lighting = findLighting(args.settings.lightingId);
    const backdrop = resolveScene(args.settings.backdropId, args.settings.customBackdrop);
    const grade = findGrade(args.settings.gradeId);
    /* The stock asks for the optics; the grain itself is added in development. */
    const stock = resolveStock(args.settings.filmStockId);
    const ratio = args.settings.aspectRatio ?? pov?.preferredRatio ?? '2:3';
    const details = [DEFAULT_DETAILS, args.settings.styleNotes?.trim(), args.correction?.trim()]
        .filter(Boolean)
        .join(', ');
    const subjectNoun = args.model.appearance.gender === 'female'
        ? 'woman'
        : args.model.appearance.gender === 'male'
            ? 'man'
            : 'person';
    const faceCount = args.faceReferenceCount ?? 0;
    const identityText = args.model.identityPrompt || buildIdentityPrompt(args.model.appearance);
    /*
     * With an identity photograph attached, the photograph is the subject and the
     * written description is only supporting detail. Opening with the text alone
     * — "photograph of a white woman with dark brown hair" — states an identity
     * before the reference is ever mentioned, and a later "match the photo" does
     * not undo it. When the two disagree, the render followed the words.
     */
    const subject = faceCount > 0 && args.afterRefusal
        ? `the model shown in ${faceCount === 1 ? 'the reference photograph' : 'the reference photographs'}`
        : faceCount > 0
            ? `the exact ${subjectNoun} shown in ${faceCount === 1 ? 'the identity photograph' : 'the identity photographs'} described below`
            : identityText;
    /*
     * A chosen pose is written for the whole body, and a crop shows part of
     * it. "Walking past with a hand in the trouser pocket" framed chest-up asks
     * for a pocket, shoes and a bag that cannot be in the picture, and a
     * renderer given a request it cannot satisfy is as likely to decline as to
     * guess. Saying what the crop keeps resolves it.
     */
    const chosenPose = args.afterRefusal ? undefined : pose;
    const posePrompt = chosenPose
        ? pov?.isCrop
            ? `${stripStop(chosenPose.prompt)}, seen in a ${(pov.name ?? 'cropped').toLowerCase()} crop: only what of this pose falls inside the frame is shown, and whatever falls outside it is left out rather than squeezed in`
            : chosenPose.prompt
        : (pov?.pose ?? 'relaxed natural stance, neutral expression, looking at camera');
    const wearsSunglasses = /sunglass/i.test(`${args.accessories} ${args.textOnlyItems.join(' ')}`);
    /*
     * A photograph being recreated decides the pose, the camera, the light and
     * — unless one was chosen — the expression. Its props come with the pose:
     * the coffee cup is part of how her hand is held.
     */
    const read = args.afterRefusal ? undefined : args.reference?.read;
    const readPose = read
        ? [
            // A pose added by hand has no words: the photograph alone says it.
            stripStop(read.pose) || 'exactly the pose of the photograph being recreated',
            read.props.length ? `holding or using ${joinList(read.props)}` : '',
        ]
            .filter(Boolean)
            .join(', ')
        : undefined;
    const chosenExpression = args.settings.expression && args.settings.expression !== 'auto'
        ? findExpression(args.settings.expression)?.prompt
        : read?.expression || findExpression(args.settings.expression)?.prompt;
    const blocks = {
        subject,
        ...(pov?.lead ? { lead: pov.lead } : {}),
        outfit: args.outfitParagraph,
        accessories: args.accessories,
        backdrop: '',
        pose: readPose ?? posePrompt,
        expression: args.afterRefusal ? undefined : chosenExpression || undefined,
        attitude: args.afterRefusal ? undefined : findAttitude(args.settings.attitude)?.prompt || undefined,
        lighting: (args.reference?.keepLight !== false ? read?.lighting : undefined) ||
            lighting?.prompt ||
            'soft, even studio lighting, clean commercial look',
        camera: [
            read?.framing || pov?.camera || 'eye-level, full-body framing, sharp focus',
            copySpaceClause(args.settings),
        ]
            .filter(Boolean)
            .join(', '),
        details: read?.mood ? `${details}, ${stripStop(read.mood)}` : details,
        quality: [QUALITY_TAIL, grade?.prompt, stock.prompt, ratioPhrase(ratio)].filter(Boolean).join(', '),
        referenceBindings: buildReferenceBindings(subjectNoun, args.referenceGarments, args.textOnlyItems, args.faceReferenceCount ?? 0, args.hasBackgroundReference ?? false, args.referenceGarments.map((g) => viewKindOf(g, args.povId)), args.correctingPreviousFrame ?? false, args.afterRefusal ?? false, wearsSunglasses && faceCount > 0, args.hasContinuityAnchor ?? false, args.bodyViews, args.reference?.attached
            ? { keepScene: args.reference.keepScene, keepLight: args.reference.keepLight }
            : undefined, args.garmentExtras ??
            extraGarmentReferences(args.referenceGarments, args.povId).map((d) => ({
                index: d.index,
                kind: d.view.kind,
                note: d.view.note,
            }))),
    };
    if (read && args.reference?.keepScene) {
        blocks.backdrop = `The setting is the place in the photograph being recreated: ${stripStop(read.setting)}.`;
    }
    else if (args.hasBackgroundReference) {
        // The place has a photograph: the binding below names it, and its words would only paraphrase it.
        blocks.backdrop = '';
    }
    else if (backdrop) {
        blocks.backdrop = buildSceneClause(subjectNoun, backdrop, chosenPose?.posture ?? (read ? postureOf(read.pose) : 'standing'), chosenPose?.onGroundSurface ?? false);
    }
    if (read && args.reference?.attached && args.reference.transfer) {
        const garments = args.referenceGarments.length;
        const bodies = args.bodyViews?.length ?? 0;
        const post = garments + faceCount + bodies + (args.hasContinuityAnchor ? 1 : 0) + 1;
        blocks.transfer = poseTransferLines({
            subjectNoun,
            post,
            garments,
            faces: faceCount ? [garments + 1, garments + faceCount] : undefined,
            bodies: bodies ? [garments + faceCount + 1, garments + faceCount + bodies] : undefined,
            background: args.hasBackgroundReference ? post + 1 : undefined,
            props: read.props,
            keepLight: args.reference.keepLight !== false,
            keepScene: args.reference.keepScene,
        });
    }
    if (blocks.transfer?.length)
        return { prompt: composeTransferPrompt(blocks, args, lighting?.prompt, ratio), blocks };
    return { prompt: composePrompt(blocks), blocks };
}
/**
 * A pose transfer, said with the photographs and as few words as will do.
 * The pose, the camera, the face and the clothes are all in the images, so
 * nothing the reader wrote about the pose photograph is sent: a sentence
 * about "a woman sitting on a ledge" is a second, vaguer version of the
 * picture beside it. What is left is what no image shows — a scene chosen
 * from the studio's list, a light chosen over the place's own, a chosen
 * expression, and what the critic asked to correct.
 */
function composeTransferPrompt(blocks, args, chosenLight, ratio) {
    const keepLight = args.reference?.keepLight !== false;
    const chosenExpression = args.settings.expression && args.settings.expression !== 'auto'
        ? findExpression(args.settings.expression)?.prompt
        : undefined;
    const lines = [
        ...(blocks.transfer ?? []).slice(0, -1),
        ...(blocks.backdrop ? [`The new place: ${stripStop(blocks.backdrop)}.`] : []),
        ...(!keepLight && chosenLight && args.settings.lightingId !== 'match-setting'
            ? [`Lighting: ${stripStop(chosenLight)}.`]
            : []),
        ...(chosenExpression ? [`Expression: ${stripStop(chosenExpression)}.`] : []),
        ...(blocks.accessories ? [ensureStop(blocks.accessories)] : []),
        // With no garment photographs, the stylist's words are all there is of the outfit.
        ...(!args.referenceGarments.length && blocks.outfit ? [ensureStop(blocks.outfit)] : []),
        ...(args.correction?.trim() ? [`Correct this: ${stripStop(args.correction)}.`] : []),
        `A real photograph: natural skin texture, true-to-life fabric, sharp focus, ${ratioPhrase(ratio)}.`,
    ];
    if (blocks.referenceBindings.length)
        lines.push('', ...blocks.referenceBindings);
    return lines.join('\n').trim();
}
/**
 * Room left in the frame for type.
 *
 * Phrased as composition rather than as "leave space for text": a renderer
 * told about text tends to draw some. What it is asked for is an off-centre
 * subject and an uncluttered area, which is the same picture without the
 * lettering.
 */
export function copySpaceClause(settings) {
    switch (settings.copySpace) {
        case 'left':
            return 'the subject composed to the right of frame, the left third left open and uncluttered';
        case 'right':
            return 'the subject composed to the left of frame, the right third left open and uncluttered';
        case 'top':
            return 'the subject composed low in frame, the upper third left open and uncluttered';
        default:
            return '';
    }
}
/**
 * What the renderer will actually be shown for this garment at this framing —
 * the same choice `referenceAssetFor` makes, named rather than resolved.
 */
export function viewKindOf(garment, povId) {
    const want = povId ? findPov(povId)?.garmentView : undefined;
    const chosen = pickView(garment, want);
    return garmentViews(garment).find((v) => v.assetId === chosen)?.kind ?? 'front';
}
/**
 * One line per garment naming what the analyst saw — the features a render
 * has to reproduce, and the ones it must not invent. The renderer is told
 * this list; the critic is given the same list to check. A garment not yet
 * analysed gets the only instruction there is.
 */
export function garmentChecklist(garments) {
    return garments.flatMap((g) => {
        const a = g.analysis;
        if (!a)
            return [`${g.label}: match the reference photograph exactly`];
        const parts = [
            a.colors?.length ? `colours ${a.colors.join(', ')}` : '',
            a.patterns && a.patterns !== 'none' ? `pattern: ${a.patterns}` : '',
            // The size and density of the print, fenced like the length: a pattern
            // named but not sized comes back smaller and sparser.
            a.printScale
                ? `print scale: ${a.printScale} — the same motif size and density, not smaller or sparser`
                : '',
            a.fabric ? `fabric ${a.fabric}` : '',
            a.texture ? `texture ${a.texture}` : '',
            a.silhouette ? `silhouette ${a.silhouette}` : '',
            /*
             * Length as a landmark on the body, said outright and fenced on both
             * sides. "Hip-length" inside a silhouette clause was read as a mood;
             * "ends at the hip bone — no longer, no shorter" is read as a rule.
             */
            a.lengthLandmark && LENGTH_PHRASES[a.lengthLandmark]
                ? `length: ${LENGTH_PHRASES[a.lengthLandmark]} — no longer, no shorter${a.length ? ` (${a.length})` : ''}`
                : '',
            a.sleeves && SLEEVE_PHRASES[a.sleeves]
                ? `sleeves: ${SLEEVE_PHRASES[a.sleeves]} — no longer, no shorter`
                : '',
            a.countables?.length ? `exactly ${a.countables.map((c) => `${c.count} ${c.feature}`).join(', ')}` : '',
            a.construction?.length ? `construction: ${a.construction.join('; ')}` : '',
            // "None" is a feature too: the buttons a renderer adds to an open front
            // are the commonest invention the critic finds.
            a.closures
                ? a.closures === 'none'
                    ? 'no closures — no buttons, zips or ties'
                    : `closures ${a.closures}`
                : '',
            a.hardware && a.hardware !== 'none' ? `hardware ${a.hardware}` : '',
            a.finish ? `finish ${a.finish}` : '',
            a.back ? `back: ${a.back}` : '',
        ].filter(Boolean);
        return [`${a.name || g.label}: ${parts.join(' · ')}`];
    });
}
/**
 * Orders garments the way they should be attached as reference images:
 * outermost/most distinctive first, matching how people describe a look.
 */
export function orderGarmentsForReference(garments) {
    return [...garments].sort((a, b) => b.layer - a.layer || a.label.localeCompare(b.label));
}
export { QUALITY_TAIL, DEFAULT_DETAILS };
/**
 * Prompt for a model's reference portrait.
 *
 * This image is not a fashion shot — it is the face reference attached to every
 * later render to hold the identity steady. So it deliberately strips out
 * anything that would compete with the garments: plain clothing, plain
 * background, even lighting, no styling. A striking portrait here would leak
 * its wardrobe and mood into the whole shoot.
 */
export function buildPortraitPrompt(identityPrompt, referenceCount = 0) {
    // With an existing reference this is another angle of the same person, not a
    // new person who happens to match the words.
    const subject = referenceCount > 0
        ? `the exact person shown in the ${referenceCount === 1 ? 'reference photograph' : 'reference photographs'} — ${stripStop(identityPrompt)}`
        : stripStop(identityPrompt);
    const identityLines = referenceCount > 0
        ? [
            `The ${referenceCount === 1 ? 'attached photograph is' : 'attached photographs are'} the person to reproduce: same facial structure, same eyes, nose and mouth, same skin tone, same hair. It must be recognisably the same individual, simply re-photographed under the lighting described above.`,
            'Where the written description and the photograph disagree, the photograph is correct.',
        ]
        : [];
    return [
        `Photorealistic studio reference portrait of ${subject}.`,
        'Head-and-shoulders framing, squared to camera, relaxed neutral expression, eyes on the lens.',
        'Wearing a plain, unbranded light grey crew-neck t-shirt.',
        'Pose: upright and still, shoulders level, hair worn down and unstyled.',
        'Lighting: soft, even, shadowless studio lighting on a seamless mid-grey background.',
        'Camera: eye-level, 85mm lens, sharp focus on the eyes, no depth-of-field blur on the face.',
        'Details: natural skin texture with visible pores, realistic hair detail, authentic proportions, no retouching, no stylization, no makeup beyond what is described.',
        'Photorealistic, hyperdetailed, neutral colour grading, casting-call reference photograph, 4:5 portrait ratio.',
        ...identityLines,
    ].join('\n');
}
/**
 * A full-body reference photograph of a model from one side, drawn from her
 * portraits — the turnaround a casting agency would send.
 *
 * Plain fitted clothes and a plain grey sweep, so nothing in it competes with
 * what it is for: her build, her proportions, and her hair from that side.
 */
export function buildTurnaroundPrompt(identityPrompt, view, referenceCount = 0) {
    const subject = referenceCount > 0
        ? `the exact person shown in the ${referenceCount === 1 ? 'reference photograph' : 'reference photographs'} — ${stripStop(identityPrompt)}`
        : stripStop(identityPrompt);
    const turn = {
        front: 'Standing squarely facing the camera, arms relaxed at her sides, weight even on both feet, eyes on the lens.',
        profile: 'Standing in strict profile, her left side to the camera, facing the left edge of the frame, arms relaxed at her sides; one ear, one shoulder, the line of the nose and chin visible.',
        back: 'Standing with her back fully to the camera, facing directly away, arms relaxed at her sides; the back of her head, her hair and her back towards the lens, no face visible.',
    };
    return [
        `Photorealistic full-body casting reference photograph of ${subject}.`,
        turn[view],
        'Head to toe in frame with a little space above the head and below the feet, the whole figure sharp.',
        /*
         * The beige bodysuit of the neutral pose every pose photograph is first
         * made into: her figure is sent beside that neutral body, and reads as the
         * same body in the same suit rather than as an outfit to copy onto it.
         */
        'Wearing only a plain, unbranded, fitted, long-sleeved beige bodysuit — no other clothing, no shoes, no jewellery — barefoot, so her build and proportions read clearly. Hair worn down and unstyled.',
        'Lighting: soft, even, shadowless studio lighting on a seamless pure white background and floor.',
        'Camera: at waist height, 50mm lens, straight-on, no wide-angle distortion, true proportions.',
        'Details: natural skin texture, realistic hair, authentic body proportions, no retouching, no stylization.',
        'Photorealistic, neutral colour grading, model agency turnaround photograph, 2:3 portrait ratio.',
        ...(referenceCount > 0
            ? [
                `The ${referenceCount === 1 ? 'attached photograph is' : 'attached photographs are'} the person to reproduce: the same face, skin tone, hair colour and hair length. It must be recognisably the same individual, photographed from this side.`,
            ]
            : []),
    ].join('\n');
}
//# sourceMappingURL=prompt.js.map